/**
 * The Guide page's content — pure data, no React, no DOM, so the rail, the
 * deep links and the copy tests cannot disagree. Every sentence a user reads
 * on the Guide lives here (spec §2.2). Keep it plain: no engine names, no
 * dev-speak, one idea per block.
 */
import type { TourStep } from "../tour/steps.ts";

export type GuideSectionId =
  | "your-brand"
  | "make-a-video"
  | "review-it"
  | "studio"
  | "test-vs-approve"
  | "post-it"
  | "autopilot"
  | "credits"
  | "security";

export type GuideBlock =
  | { kind: "p"; text: string }
  /** A pointer: WHERE in the app something is, then what happens there. */
  | { kind: "where"; label: string; text: string }
  | { kind: "list"; items: string[] };

export interface GuideSection {
  id: GuideSectionId;
  /** ≤ 6 words. */
  title: string;
  /** One sentence: what this part of the app is for. */
  lede: string;
  blocks: GuideBlock[];
  /** "Show me" jumps into the tour at this step. Must exist in TOUR_STEPS. */
  tourStepId?: TourStep["id"];
}

export const GUIDE_SECTIONS: readonly GuideSection[] = [
  {
    id: "your-brand",
    title: "Your brand",
    lede: "A brand is everything a video needs to look like yours.",
    tourStepId: "brand-chip",
    blocks: [
      { kind: "p", text: "It stores the name and language, the logo, the colours, the fonts, the caption size and position, the speaker card, the sign-off text and the posting connection. Every video you make renders with the brand that is active when you start it." },
      { kind: "where", label: "The chip at the top right", text: "Click it to switch between your brands. The pencil next to a brand edits it. \"Set up a brand\" adds a new one." },
      { kind: "where", label: "Archive", text: "At the bottom of Edit brand. An archived brand leaves your list; its videos, posts and posting connection stay, and one click in Settings › Brand brings it back. Nothing is ever deleted from here." },
    ],
  },
  {
    id: "make-a-video",
    title: "Make a video",
    lede: "Three ways in, one build, about five to ten minutes.",
    tourStepId: "nav-new",
    blocks: [
      { kind: "list", items: [
        "Paste a link and pick the moment you want.",
        "Upload a vertical clip you already have.",
        "Try a sample to see your brand on a real video first.",
      ] },
      { kind: "where", label: "Find scenes", text: "Paste a link with no moment chosen and the editor reads the whole talk and proposes scenes. They wait in the Backlog until you pick one to build." },
      { kind: "where", label: "Render queue", text: "Watch each step as the video builds. Builds run one at a time, so a queue is normal." },
      { kind: "p", text: "Nothing publishes on its own. A finished video waits in the Library." },
    ],
  },
  {
    id: "review-it",
    title: "Review it",
    lede: "The Library holds every finished video, newest first.",
    tourStepId: "library-card",
    blocks: [
      { kind: "where", label: "The video page", text: "Title, status, the preview and the post caption. Download the file, open it in the Studio, or schedule a post." },
      { kind: "where", label: "Details", text: "How the video was made, what was asked for, the transcript, the log and the files. Nothing you need day to day, everything you need when something looks off." },
    ],
  },
  {
    id: "studio",
    title: "Change anything in the Studio",
    lede: "Everything about a finished video can still change.",
    tourStepId: "open-studio",
    blocks: [
      { kind: "where", label: "The panel on the left", text: "Captions, logo, speaker, footage and extra text, one tab each. Click a caption in the timeline to edit that one." },
      { kind: "where", label: "The chat", text: "Select a caption and say what feels off, or describe a change to the whole look. Every change previews at once and can be undone." },
      { kind: "where", label: "Edit this clip", text: "Captions and text, start and end, sound, camera and framing, a fresh cut, and earlier versions. Each one re-renders the video when you confirm." },
      { kind: "p", text: "Edits save as you go. The counter at the top right is your safety net: it says how many edits there are and whether they are saved." },
      { kind: "p", text: "Undo takes back the last edit. Reset clears every design change on this video and goes back to the brand's design; caption text you already approved stays." },
    ],
  },
  {
    id: "test-vs-approve",
    title: "Test render vs Approve & render",
    lede: "One is a peek, the other is the real thing.",
    tourStepId: "studio-test-render",
    blocks: [
      { kind: "where", label: "Test render", text: "Renders about six seconds around the selected caption through the real renderer and plays it here. Saves nothing, changes nothing, costs no credit. Use it to check a font or a colour before you commit." },
      { kind: "where", label: "Approve & render", text: "Re-renders this video with every edit applied. The previous version stays under Versions. Caption text edits are baked into this video only." },
      { kind: "where", label: "The checkbox in the Approve dialog", text: "Tick \"Also make this the brand design\" and every future video on this brand renders with it. Untick it and the brand keeps its current design. Videos already rendered never change." },
      { kind: "where", label: "Save as my style", text: "The same as the checkbox, without a render." },
    ],
  },
  {
    id: "post-it",
    title: "Post it",
    lede: "Posting goes through your Buffer connection, one per brand.",
    tourStepId: "schedule-post",
    blocks: [
      { kind: "where", label: "Schedule post", text: "On the video page. Pick the channels, the time and the caption. The Calendar shows everything scheduled." },
      { kind: "where", label: "Settings › Posting", text: "Connect Buffer for the active brand and set the default post caption." },
      { kind: "p", text: "\"Automatic\" posts by itself at the scheduled time. \"Notification\" sends you a reminder and you post by hand." },
    ],
  },
  {
    id: "autopilot",
    title: "Autopilot",
    lede: "A switch per brand that builds 1 to 3 videos a day on its own.",
    blocks: [
      { kind: "p", text: "Every ten minutes it checks whether it may work: only when nothing else is building, only until the day's count is reached, and only from sources it found itself. Bookmarked and followed shows come first, then your own back catalogue." },
      { kind: "list", items: [
        "It builds only scenes it is confident in, with a clean ending.",
        "It never builds a near-duplicate of a video you already have.",
        "It never publishes. Everything lands in the Library as a draft for you to review.",
        "After repeated failures it stands down for the rest of the day.",
        "The day resets at midnight, Paris time.",
      ] },
      { kind: "where", label: "Settings › Autopilot", text: "Turn it on per brand, choose how many videos a day (1 to 3), and read what it did under Recent activity." },
    ],
  },
  {
    id: "credits",
    title: "Credits and your own key",
    lede: "Every build and every chat turn costs a little; here is how that is paid.",
    blocks: [
      { kind: "where", label: "Platform credit", text: "A running balance of what your videos and chats cost. It can go below zero and a build is never stopped for it; the chip just turns amber until credit is added." },
      { kind: "where", label: "Your own key", text: "Paste your own model key in Settings › AI & credits and usage is billed to you directly. The key is stored encrypted and verified before it is saved." },
      { kind: "where", label: "Usage & cost", text: "This month, the last thirty days, by kind and by day." },
    ],
  },
  {
    id: "security",
    title: "Security",
    lede: "Two-factor sign-in with an authenticator app.",
    blocks: [
      { kind: "where", label: "Settings › Security", text: "Turn on two-factor, scan the code with your authenticator app, confirm once. From then on sign-in asks for the six-digit code." },
      { kind: "p", text: "Lost the device? Contact us and we will reset two-factor for you." },
    ],
  },
];

export function findGuideSection(id: string | null): GuideSection | null {
  if (!id) return null;
  return GUIDE_SECTIONS.find((s) => s.id === id) ?? null;
}
